import { useEffect, useState } from "react";
import { CheckCircle2, X, ScanSearch, Loader2, Link2, LayoutTemplate } from "lucide-react";
import useBindings from "@/lib/useBindings";

const KIND_STYLE = {
  headline:   { color: "#B5462F", label: "Headline" },
  body:       { color: "#1F6B6B", label: "Body copy" },
  disclaimer: { color: "#6B6358", label: "Disclaimer" },
  photo:      { color: "#2E6FB5", label: "Photography" },
  logo:       { color: "#C28A2E", label: "Logo" },
  shape:      { color: "#8A5FB0", label: "Shape / device" },
  background: { color: "#3D7A5A", label: "Background" },
  unknown:    { color: "#9A9A9A", label: "Unclassified" },
};

const DERIVE_FLAG = {
  ok:        { color: "#1F6B6B", label: "Compliant" },
  stretch:   { color: "#C28A2E", label: "Stretches a rule" },
  violation: { color: "#B5462F", label: "Violates a rule" },
  unknown:   { color: "#6B6358", label: "Unclear" },
};

// Which Brand Kit binding registry a derived slot maps to, by kind.
// Text slots bind a font role; backgrounds bind a color role; photo/unknown
// have no registry binding (photography resolves from the brand library).
const KIND_ASSET_CLASS = {
  headline: "font", body: "font", disclaimer: "font",
  background: "color", logo: "logo", shape: "shape",
  photo: null, unknown: null,
};

const NO_BINDING_HINT = {
  photo: "Photography resolves from the brand library, not a binding.",
  unknown: "Set a slot kind above to map it to a binding.",
};

function SlotBindingSelect({ assetClass, brandId, value, suggested, onChange, testid }) {
  const { bindings, loading } = useBindings(assetClass, brandId);
  if (loading) return <span className="text-[10px] text-[#6B6358]">Loading {assetClass} bindings…</span>;
  if (!bindings.length) {
    return (
      <span className="text-[10px] text-[#6B6358] italic">
        No {assetClass} bindings yet — author one in the Brand Kit.
      </span>
    );
  }
  return (
    <div className="flex-1 min-w-0">
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] px-2 py-1 w-full"
      >
        <option value="">— map to {assetClass} binding —</option>
        {bindings.map((b) => (
          <option key={b.slug} value={b.slug}>{b.label} · {b.slug}</option>
        ))}
      </select>
      {!value && suggested && (
        <p className="text-[9px] text-[#6B6358] mt-0.5">AI suggested: {suggested}</p>
      )}
    </div>
  );
}

export default function DerivePreviewModal({ state, brandId, onClose, onRetry, onSave, onPromote }) {
  const { program, status, data, error, saved } = state;
  const flags = data?.rule_flags || [];
  const busy = status === "loading" || status === "deriving";
  const failed = status === "skipped" || status === "error";

  const [editSlots, setEditSlots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promotedOk, setPromotedOk] = useState(false);

  useEffect(() => {
    if (status === "ready" && data?.slots) {
      setEditSlots(data.slots.map((s) => ({ binding_slug: "", ...s })));
      setSavedOk(false);
    }
  }, [status, data]);

  const setKind = (rid, kind) =>
    setEditSlots((sl) => sl.map((s) => {
      if (s.region_id !== rid) return s;
      const sameClass = KIND_ASSET_CLASS[s.kind] === KIND_ASSET_CLASS[kind];
      return { ...s, kind, binding_slug: sameClass ? s.binding_slug : "" };
    }));
  const setBinding = (rid, binding_slug) =>
    setEditSlots((sl) => sl.map((s) => (s.region_id === rid ? { ...s, binding_slug } : s)));
  const removeSlot = (rid) => setEditSlots((sl) => sl.filter((s) => s.region_id !== rid));

  const summary = editSlots.reduce((acc, s) => {
    acc[s.kind] = (acc[s.kind] || 0) + 1; return acc;
  }, {});
  const mappableCount = editSlots.filter((s) => KIND_ASSET_CLASS[s.kind]).length;
  const mappedCount = editSlots.filter((s) => KIND_ASSET_CLASS[s.kind] && s.binding_slug).length;

  // The layout must be saved before it can be promoted; once edits are made the
  // designer must re-save (so the promoted template reflects the latest mapping).
  const isSaved = saved || savedOk;
  const alreadyPromoted = !!program?.template_id;

  const doSave = async () => {
    setSaving(true);
    try {
      await onSave(program, editSlots, {
        page_image: data.page_image, page_width: data.page_width,
        page_height: data.page_height, page_count: data.page_count,
      });
      setSavedOk(true);
      setPromotedOk(false); // a re-save invalidates the prior promotion
    } finally {
      setSaving(false);
    }
  };

  const doPromote = async () => {
    setPromoting(true);
    try {
      const fresh = await onPromote(program);
      if (fresh?.template_id) setPromotedOk(true);
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div
      data-testid="derive-preview-modal"
      className="fixed inset-0 z-50 bg-[#1A1F2C]/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#F8F5EE] rounded-xl w-full max-w-5xl my-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-[#1A1F2C]/10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#B5462F]">Derived layout · verify · map · promote</p>
            <h3 className="font-serif text-2xl mt-0.5">{program?.name}</h3>
            <p className="text-xs text-[#6B6358] mt-1 max-w-xl">
              Re-tag any mis-classified slot, map each to a Brand Kit binding, save, then
              promote the layout into a real template the AI Assembly Agent will use.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {status === "ready" && (
              <>
                {saved && !savedOk && (
                  <span className="text-[10px] uppercase tracking-[0.14em] text-[#1F6B6B] inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> Saved
                  </span>
                )}
                <button
                  data-testid="derive-preview-rederive"
                  onClick={onRetry}
                  className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-[#1A1F2C]/30 rounded-full text-[#1A1F2C] inline-flex items-center gap-1.5"
                >
                  <ScanSearch size={12} /> Re-derive
                </button>
                <button
                  data-testid="derive-save-layout"
                  onClick={doSave}
                  disabled={saving || editSlots.length === 0}
                  className="px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] bg-[#1A1F2C] text-[#F8F5EE] rounded-full inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {saving ? "Saving…" : savedOk ? "Saved ✓" : "Save layout"}
                </button>
                <button
                  data-testid="derive-promote-template"
                  onClick={doPromote}
                  disabled={promoting || !isSaved}
                  title={!isSaved ? "Save the layout first" : "Generate an assembly template from this layout"}
                  className="px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] bg-[#C28A2E] text-white rounded-full inline-flex items-center gap-1.5 disabled:opacity-40"
                >
                  <LayoutTemplate size={12} />
                  {promoting ? "Promoting…" : (promotedOk || alreadyPromoted) ? "Template ready ✓" : "Promote to template"}
                </button>
              </>
            )}
            <button
              data-testid="derive-preview-close"
              onClick={onClose}
              className="p-2 text-[#6B6358] hover:text-[#1A1F2C]"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {(promotedOk || alreadyPromoted) && status === "ready" && (
            <p data-testid="derive-promoted-banner" className="text-xs text-[#7a5a16] bg-[#C28A2E]/12 border border-[#C28A2E]/30 rounded-md px-3 py-2 mb-4 inline-flex items-center gap-2">
              <LayoutTemplate size={13} />
              This program is now wired to an assembly template. New Studio projects using this program will build on the derived layout.
            </p>
          )}
          {busy ? (
            <p className="text-sm text-[#6B6358] flex items-center gap-2 py-10 justify-center">
              <Loader2 size={16} className="animate-spin" />
              {status === "deriving"
                ? "Deriving layout from the creative sample… this can take up to a minute."
                : "Loading…"}
            </p>
          ) : failed ? (
            <div className="py-8 text-center">
              <p className="text-sm text-[#B5462F]">{error}</p>
              <button
                data-testid="derive-preview-retry"
                onClick={onRetry}
                className="mt-3 px-4 py-2 text-xs uppercase tracking-[0.18em] border border-[#1A1F2C]/30 rounded-full"
              >
                Try again
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
              {/* Overlay canvas */}
              <div>
                <div className="relative inline-block w-full border border-[#1A1F2C]/15 rounded-lg overflow-hidden bg-white">
                  {data?.page_image && (
                    <img
                      src={data.page_image}
                      alt="Creative sample page"
                      data-testid="derive-preview-image"
                      className="block w-full"
                    />
                  )}
                  {editSlots.map((s) => {
                    const st = KIND_STYLE[s.kind] || KIND_STYLE.unknown;
                    return (
                      <div
                        key={s.region_id}
                        data-testid={`derive-slot-${s.region_id}`}
                        className="absolute"
                        style={{
                          left: `${s.bbox.x * 100}%`,
                          top: `${s.bbox.y * 100}%`,
                          width: `${s.bbox.w * 100}%`,
                          height: `${s.bbox.h * 100}%`,
                          border: `2px solid ${st.color}`,
                          backgroundColor: `${st.color}1f`,
                        }}
                        title={`${st.label}${s.label ? " · " + s.label : ""}`}
                      >
                        <span
                          className="absolute -top-0.5 left-0 text-[9px] uppercase tracking-[0.1em] px-1 py-0.5 text-white whitespace-nowrap"
                          style={{ backgroundColor: st.color, transform: "translateY(-100%)" }}
                        >
                          {st.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {data?.page_count > 1 && (
                  <p className="text-[11px] text-[#6B6358] mt-2">
                    Showing page 1 of {data.page_count}. Multi-page derivation arrives in a later step.
                  </p>
                )}
              </div>

              {/* Right rail */}
              <div className="space-y-6">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B6358] mb-2">
                    Detected slots ({editSlots.length})
                  </p>
                  {mappableCount > 0 && (
                    <p data-testid="derive-mapped-count" className="text-[11px] text-[#1F6B6B] mb-2 inline-flex items-center gap-1">
                      <Link2 size={11} /> {mappedCount}/{mappableCount} slots mapped to a binding
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {Object.entries(summary).map(([kind, n]) => {
                      const st = KIND_STYLE[kind] || KIND_STYLE.unknown;
                      return (
                        <span
                          key={kind}
                          data-testid={`derive-summary-${kind}`}
                          className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${st.color}1a`, color: st.color }}
                        >
                          <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: st.color }} />
                          {st.label} · {n}
                        </span>
                      );
                    })}
                  </div>
                  <div className="space-y-1.5 max-h-[260px] overflow-auto pr-1">
                    {editSlots.map((s) => {
                      const st = KIND_STYLE[s.kind] || KIND_STYLE.unknown;
                      return (
                        <div
                          key={s.region_id}
                          data-testid={`derive-slotrow-${s.region_id}`}
                          className="flex items-start gap-2 text-sm border-b border-[#1A1F2C]/5 pb-1.5"
                        >
                          <span className="w-2.5 h-2.5 rounded-sm mt-2 shrink-0" style={{ backgroundColor: st.color }} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <select
                                data-testid={`derive-slot-kind-${s.region_id}`}
                                value={s.kind}
                                onChange={(e) => setKind(s.region_id, e.target.value)}
                                className="text-xs border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] px-2 py-1 flex-1"
                              >
                                {Object.keys(KIND_STYLE).map((k) => (
                                  <option key={k} value={k}>{KIND_STYLE[k].label}</option>
                                ))}
                              </select>
                              <button
                                data-testid={`derive-slot-remove-${s.region_id}`}
                                onClick={() => removeSlot(s.region_id)}
                                title="Remove this slot"
                                className="p-1 text-[#B5462F] hover:text-[#8f3624]"
                              >
                                <X size={14} />
                              </button>
                            </div>
                            {s.text && <p className="text-xs text-[#6B6358] truncate mt-0.5">“{s.text}”</p>}
                            <div className="mt-1 flex items-start gap-1.5">
                              <Link2 size={12} className="text-[#6B6358] shrink-0 mt-1.5" />
                              {KIND_ASSET_CLASS[s.kind] ? (
                                <SlotBindingSelect
                                  assetClass={KIND_ASSET_CLASS[s.kind]}
                                  brandId={brandId}
                                  value={s.binding_slug}
                                  suggested={s.suggested_binding}
                                  onChange={(slug) => setBinding(s.region_id, slug)}
                                  testid={`derive-slot-binding-${s.region_id}`}
                                />
                              ) : (
                                <span className="text-[10px] text-[#6B6358] italic mt-1">
                                  {NO_BINDING_HINT[s.kind] || "No binding for this slot kind."}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {flags.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B6358] mb-2">
                      Brand-rule adherence
                    </p>
                    <div className="space-y-1.5">
                      {flags.map((f, i) => {
                        const fl = DERIVE_FLAG[f.status] || DERIVE_FLAG.unknown;
                        return (
                          <div
                            key={i}
                            data-testid={`derive-flag-${i}`}
                            className="border rounded-md px-3 py-2"
                            style={{ backgroundColor: `${fl.color}14`, borderColor: `${fl.color}40` }}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] uppercase tracking-[0.14em] font-medium" style={{ color: fl.color }}>
                                {fl.label}
                              </span>
                              {f.rule_type && <span className="text-[10px] text-[#6B6358]">· {f.rule_type}</span>}
                            </div>
                            {f.note && <p className="text-xs text-[#3D3A35] mt-0.5">{f.note}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {data?.note && (
                  <p className="text-xs text-[#6B6358] italic border-t border-[#1A1F2C]/10 pt-3">{data.note}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
